import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/shared/ui/button';
import { t } from '@/i18n';
import { CONSENT_PROMPT_EVENT, shouldAskForConsent, writeConsent } from '@/shared/lib/cookie-consent';
import { isPrerenderRuntime } from '@/shared/lib/prerender-user-agent';

/**
 * TRAD-1155 — 쿠키/추적 동의 배너.
 *
 * ── 🔴 이 컴포넌트가 하지 **않는** 일 ────────────────────────────────────────
 * 추적을 끄고 켜지 않는다. 그건 `shared/lib/cookie-consent` 의 상태를 읽는
 * `shared/configs/firebase.ts` 와 `components/ad/AdBanner.tsx` 가 한다. 여기는 **묻고
 * 저장하는 것까지**다. 이렇게 나눈 이유는 배너를 안 렌더하는 화면에서도(프리렌더 등) 게이트가
 * 그대로 걸리게 하기 위해서다 — **배너가 곧 게이트이면, 배너가 없는 경로에서 추적이 샌다.**
 *
 * ── 신규 컴포넌트인 사유 (UI 일관성 룰 1) ────────────────────────────────────
 * "닫을 수 있는 고지 배너"가 레포에 없다. 착수 시 실측:
 *   `fixed bottom` 계열 4곳 = CalendarFilterDrawer(드로어) · BottomTabBar(내비)
 *   · LeaderboardPage(페이지 전용 바) · PolymarketListPage(FAB) — 넷 다 성격이 다르다.
 * 그래서 레이아웃은 **BottomTabBar 규격을 복제**하고(아래 className 주석) 버튼은 공용
 * `Button` 을 쓴다. 새로 그린 것은 없다.
 *
 * ── z-index / 위치 ──────────────────────────────────────────────────────────
 * `MainLayout.tsx` 가 정리해 둔 척도를 따른다: 페이지 내부 sticky 는 최대 **30**, 헤더가
 * **35**, 전체화면 오버레이(드로어·모바일 메뉴)가 **40 이상**, `BottomTabBar` 가 **50**.
 *
 * 🔴 이 배너는 **60**이다. 그 척도에서 가장 위인데, 의도한 것이다 — **법적 고지는 무엇에도
 * 가려지면 안 된다.** 다만 `BottomTabBar`(z-50, `lg:hidden`)를 덮어버리면 모바일에서 내비를
 * 먹으므로, 탭바가 있는 구간(`lg` 미만)에서는 `bottom-16` 으로 탭바 위에 띄우고 데스크톱
 * (`lg` 이상, 탭바 없음)에서만 바닥에 붙인다. **가리는 대신 비켜서는 방식**이다.
 * ※ `MainLayout` 주석의 "30 넘게 주지 마라"는 **페이지 내부** sticky/fixed 에 대한 것이고,
 *   이건 전역 고지라 그 대상이 아니다.
 *
 * 🔴 **여유가 7px 뿐이다 — 문구를 늘리기 전에 읽어라.**
 * 2026-09-10 실화면 실측(390×844, 로컬 dev):
 *   배너 `bottom=780` (높이 139 = 제목 1줄 + 본문 3줄 + 버튼행) · 탭바 `top=787` → **간격 7px**
 * `bottom-16`(64px)은 **탭바 높이 57px 에 맞춘 고정값**이라, 배너가 아래로 자라는 게 아니라
 * **위로** 자란다 — 그래서 문구가 길어져도 탭바를 침범하지는 않는다. 위험은 반대쪽이다:
 * **본문이 한 줄만 늘어도 배너가 화면의 20% 를 먹는다**(현재 139/844 ≈ 16%).
 * 🔴 문구·폰트·버튼 크기를 바꾸면 **390px 에서 다시 재라** — 아래 테스트는 className 만 보므로
 * 이 값을 지켜주지 못한다. 재는 법: 390×844 로 열어 `[role="dialog"]` 와 하단 `nav` 의
 * `getBoundingClientRect()` 를 비교(`bottom` vs `top`).
 */
const CookieConsentBanner = () => {
  // 🔴 초기값을 `false` 로 두고 마운트 후에 판정한다. 프리렌더(Node)에는 localStorage 가
  // 없어서 렌더 중 접근하면 하이드레이션 전후가 갈린다 — 정적 HTML 에 배너가 구워지는 것도 막는다.
  const [visible, setVisible] = useState(false);

  // TRAD-3792 — 첫 방문 자동 표시는 opt-in 지역(EU·EEA·UK·CH·판정 실패)뿐이다. 다른 지역은 배너 없이 수집하고,
  // 거부·변경은 푸터 «쿠키 설정»(openConsentPrompt)으로 이 배너를 다시 연다 — 모든 지역에서.
  // 🔴 TRAD-3792 — 프리렌더 브라우저에서는 자동 표시하지 않는다. 위 «초기값 false» 만으로는 못 막았다: 프리렌더는 실제 브라우저라
  // useEffect 가 돌고, 동의 전 opt-in(UTC) 상태라 배너가 뜬 채 스냅샷이 찍혀 dev·운영 정적 HTML 에 들어가 있었다(2026-10-22 실측).
  useEffect(() => {
    setVisible(shouldAskForConsent() && !isPrerenderRuntime());
    const open = () => setVisible(true);
    window.addEventListener(CONSENT_PROMPT_EVENT, open);
    return () => window.removeEventListener(CONSENT_PROMPT_EVENT, open);
  }, []);

  if (!visible) return null;

  const choose = (choice: 'granted' | 'denied') => {
    writeConsent(choice);
    setVisible(false);
  };

  return (
    <div
      data-chrome
      role="dialog"
      aria-label={t('cookie_consent.aria_label')}
      // 레이아웃 규격 출처: BottomTabBar.tsx (`fixed … left-0 right-0 bg-white dark:bg-background
      // border-t border-border safe-area-bottom`). z-index 와 bottom 오프셋만 위 주석대로 다르다.
      className="fixed left-0 right-0 bottom-16 lg:bottom-0 z-[60] bg-white dark:bg-background border-t border-border safe-area-bottom"
    >
      <div className="mx-auto max-w-screen-lg px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{t('cookie_consent.title')}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('cookie_consent.body')}{' '}
            <Link to="/privacy-policy" className="text-brand-text underline underline-offset-4">
              {t('cookie_consent.policy_link')}
            </Link>
          </p>
        </div>
        {/* 🔴 거부가 수락과 같은 무게로 보이게 둔다 — 한쪽만 눈에 띄면 동의가 자유로운 선택이 아니다. */}
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => choose('denied')}>
            {t('cookie_consent.decline')}
          </Button>
          <Button variant="default" size="sm" onClick={() => choose('granted')}>
            {t('cookie_consent.accept')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
